/// <amd-module name="DirtyDocumentServices/Services/DirtyDocumentService" />
import { equals } from "VSS/Core/Util/String";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";

export const DirtyDocumentServiceName = "DirtyDocumentService";

export interface IDirtyDocumentService extends IVssService {
    /**
     * Register a function to determine if the document is dirty
     * @param isDirtyHandler Callback to determine if a document is dirty
     * @param documentId Document id for this handler
     * @param handlerKey (Optional) unique key to identify this handler
     */
    registerHandler(isDirtyHandler: () => boolean, documentId: string, handlerKey?: string): void;

    /**
     * Unregisters all handlers attached to this document
     * @param documentId all handlers for this documentId will be unregistered
     */
    unregisterDocument(documentId: string): void;

    /**
     * Unregister a specific document handler
     * @param documentId document id
     * @param handlerKey unique handler key
     */
    unregisterHandler(documentId: string, handlerKey: string): void;

    /**
     * Returns true if any registered handler returns true
     * @param documentId (Optional) If passed, only handlers for this specific document are checked
     */
    isDocumentDirty(documentId?: string): boolean;

    /**
     * Unregister all isDirty handlers
     */
    unregisterAllHandlers(): void;
}

interface IDirtyDocumentHandler {
    isDirtyHandler: () => boolean;
    handlerKey?: string;
}

/**
 * Service to determine if the document is dirty
 */
export class DirtyDocumentService extends VssService implements IDirtyDocumentService {
    private _dirtyDocumentHandlers: { [documentId: string]: IDirtyDocumentHandler[] } = {};

    public registerHandler(isDirtyHandler: () => boolean, documentId: string, handlerKey?: string): void {
        if (!this._dirtyDocumentHandlers.hasOwnProperty(documentId)) {
            this._dirtyDocumentHandlers[documentId] = [];
        }
        this._dirtyDocumentHandlers[documentId].push({ isDirtyHandler, handlerKey });
    }

    public unregisterDocument(documentId: string): void {
        if (this._dirtyDocumentHandlers.hasOwnProperty(documentId)) {
            delete this._dirtyDocumentHandlers[documentId];
        }
    }

    public unregisterHandler(documentId: string, handlerKey: string): void {
        const handlers = this._dirtyDocumentHandlers[documentId];
        if (handlers) {
            this._dirtyDocumentHandlers[documentId] = handlers.filter(h => !h.handlerKey || h.handlerKey !== handlerKey);
        }
    }

    public unregisterAllHandlers(): void {
        for (const handlerId in this._dirtyDocumentHandlers) {
            delete this._dirtyDocumentHandlers[handlerId];
        }

        this._dirtyDocumentHandlers = {};
    }

    public isDocumentDirty(documentId?: string): boolean {
        const isDirty = (handlers: IDirtyDocumentHandler[]): boolean => handlers.some(h => h.isDirtyHandler() === true);

        if (documentId) {
            return this._dirtyDocumentHandlers.hasOwnProperty(documentId) && isDirty(this._dirtyDocumentHandlers[documentId]);
        } else {
            return Object.keys(this._dirtyDocumentHandlers).some(h => isDirty(this._dirtyDocumentHandlers[h]));
        }
    }
}

Services.add(DirtyDocumentServiceName, { serviceFactory: DirtyDocumentService });
